<script setup lang="ts">
import type { SelectionItem } from '@models/selectionItem.ts';
import SelectionItemGalleryActions from '@organisms/offer/selection-items/components/SelectionItemGalleryActions.vue';
import Button from '@components/Button.vue';
import Icon from '@components/Icon.vue';
import Typography from '@components/Typography.vue';
import { computed, toRefs } from 'vue';
import type { UUIDv4 } from '@global-types/uuid.ts';
import Carousel from '@components/offer-card/Carousel.vue';
import MediaImage from '@components/MediaImage.vue';
import type { SerializedOffer } from '@models/offer.ts';
import ButtonLink from '@components/ButtonLink.vue';
import { useSelectionItemOfferUrl } from '@composables/useSelectionItemOfferUrl.ts';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { storeToRefs } from 'pinia';
import Gallery from '@molecules/components/Gallery.vue';
import { useModal } from '@composables/useModal.ts';
import { useSelectionItemStore } from '@organisms/offer/selection-items/stores/useSelectionItemStore';
import { trans } from '@elements/translation.ts';
import SelectionItemTags from '@organisms/offer/selection-items/components/SelectionItemTags.vue';
import IconCameraOutline from '@icons/ph_camera-outline.svg';

const props = defineProps<{ selectionItem: SelectionItem }>();

const { preresizes } = storeToRefs(useSelectionItemStore());

const { selectionItem } = toRefs(props);

const { isMobileOrTablet } = storeToRefs(useScreensizeStore());
const { offerUrl, openOffer } = useSelectionItemOfferUrl(selectionItem);

const { isOpen, closeModal, openModal } = useModal();

const offer = computed<SerializedOffer>(() => props.selectionItem.offer);

const sliderImages = computed<UUIDv4[]>(() => offer.value.slider_images);
const clearImages = computed<UUIDv4[]>(() => sliderImages.value.slice(0, 12));

const contactImage = computed<UUIDv4>(() => offer.value.slider_images[offer.value.slider_images.length - 1]);

const mediaWidth = computed<number | undefined>(() => {
  if (isMobileOrTablet.value) {
    return preresizes.value.slider_mobile.width;
  }

  return preresizes.value.slider_desktop.width;
});

const mediaHeight = computed<number | undefined>(() => {
  if (isMobileOrTablet.value) {
    return preresizes.value.slider_mobile.height;
  }

  return preresizes.value.slider_desktop.height;
});

const mediaRatio = computed<[number, number] | undefined>(() => {
  if (isMobileOrTablet.value) {
    return preresizes.value.slider_mobile.ratio;
  }

  return preresizes.value.slider_desktop.ratio;
});
</script>

<template>
  <div class="selection-item-gallery">
    <Carousel>
      <MediaImage
        v-for="clearImage in clearImages"
        :key="clearImage"
        :uuid="clearImage"
        class="selection-item-gallery__picture"
        :img-class="isMobileOrTablet ? 'selection-item-gallery__img' : ''"
        :width="mediaWidth"
        :height="mediaHeight"
        :ratio="mediaRatio"
        :alt="offer.name"
        is-lazy-flickity
      />

      <div class="selection-item-gallery__contact-slide">
        <MediaImage
          :uuid="contactImage"
          class="selection-item-gallery__picture selection-item-gallery__picture--contact"
          :img-class="isMobileOrTablet ? 'selection-item-gallery__img selection-item-gallery__img--contact' : ''"
          :width="mediaWidth"
          :height="mediaHeight"
          :ratio="mediaRatio"
          is-lazy-flickity
        />

        <ButtonLink
          variant="primary"
          class="selection-item-gallery__contact-button"
          :href="offerUrl ?? ''"
          :target="isMobileOrTablet ? '_self' : '_blank'"
          @click="openOffer"
        >
          {{ trans('global.know.more') }}
        </ButtonLink>
      </div>
    </Carousel>

    <div class="selection-item-gallery__banner selection-item-gallery__banner--top">
      <div class="selection-item-gallery__left">
        <SelectionItemTags :offer="selectionItem.offer" />
      </div>

      <div class="selection-item-gallery__right">
        <SelectionItemGalleryActions :selection-item="selectionItem" />
      </div>
    </div>

    <div class="selection-item-gallery__banner selection-item-gallery__banner--bottom">
      <Button
        class="selection-item-gallery__see-pictures-button"
        @click="openModal"
      >
        <Icon
          :svg="IconCameraOutline"
          height="20"
          width="20"
          fill="black"
        />

        <Typography
          variant="body-small-bold"
        >
          {{ sliderImages.length }}
        </Typography>
      </Button>
    </div>

    <Gallery
      :media-uuids="sliderImages"
      :hash-id="offer.hash_id"
      :is-open="isOpen"
      :seller-organization-id="offer.organization.id"
      has-cta-layer
      :proposal-id="selectionItem.proposal?.id"
      @close="closeModal"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.selection-item-gallery {
  position: relative;
  border-radius: rem(8);
  overflow: hidden;
  height: rem(230);

  &__banner {
    position: absolute;
    z-index: 1;
    width: 100%;
    padding: rem(8);
    display: flex;

    &--top {
      top: 0;
      justify-content: space-between;
    }

    &--bottom {
      bottom: 0;
      justify-content: end;
    }
  }

  &__contact-slide {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__contact-overlay {
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.3);
  }

  &__contact-button {
    position: absolute;
    width: fit-content;
  }

  &__picture {
    width: 100%;
    height: rem(230);
    display: flex;
    justify-content: center;

    &--contact {
      position: absolute;
      filter: blur(rem(2));
    }
  }

  &__see-pictures-button {
    border-radius: rem(100);
    width: fit-content;
    height: rem(32);
    padding: rem(8);
    display: flex;
    align-items: center;
    gap: rem(4);

    &:hover {
      background-color: $colorGray;
    }
  }

  .flickity-cell.is-selected {
    z-index: 1;
  }

  @media screen and (min-width: $breakTablet) {
    &__picture {
      height: rem(230);
      display: block;
    }
  }
}
</style>
